import type { ChangeEvent } from "react";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "throttle-debounce";
import type {
  CompareObject,
  ComparisonObjectValue,
} from "../../../services/api/instances/comparisonAPI/types";
import type { ChatCompletionsMessageObject } from "../../../services/api/instances/completionsApi/types";
import { createStorageObjectApi } from "../../../services/lib/storageObjectManager";
import { selectCompliance } from "../../../services/redux/complianceSlice/selectors";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import {
  addNewMessageObject,
  deleteInstructions,
  generateUniqueId,
  replaceItemById,
} from "../helpers";
import {
  createChatComparison,
  updateComparisonObjectAction,
} from "../redux/actions";
import {
  selectComparisonObjects,
  selectComparisonValue,
  selectSelectedComparisonObject,
} from "../redux/selectors";
import {
  setComparisonObjects,
  setComparisonObjectValue,
  setSelectedComparisonObject,
} from "../redux/slice";
import { StorageObject } from "../../../services/api/instances/storageApi/types/object_types";
import { createNotification } from "../../../app/state/notificationSlice/slice";

export const useCompare = () => {
  const dispatch = useAppDispatch();
  const compliance = useAppSelector(selectCompliance);
  const comparisonObjects = useAppSelector(selectComparisonObjects);
  const selectedComparisonObject = useAppSelector(
    selectSelectedComparisonObject,
  );
  const objectValue = useAppSelector(selectComparisonValue);
  const [userMessage, setUserMessage] = useState<string>("");
  const [columns, setColumns] = useState<number>(
    selectedComparisonObject?.value?.llm_compare?.length as number,
  );
  const [instruction, setInstruction] = useState<string>("");
  const processingRef = useRef<CompareObject[]>();
  const { t } = useTranslation();

  const objects =
    createStorageObjectApi<ComparisonObjectValue>(comparisonObjects);

  const updateComparisonObject = useCallback(
    debounce(1000, (postObj: StorageObject<ComparisonObjectValue>) => {
      dispatch(updateComparisonObjectAction(postObj));
    }),
    [],
  );

  const updateObjectsWithNewValue = (
    object: StorageObject<ComparisonObjectValue>,
    newValue: CompareObject[],
  ) => {
    const newComparisonObject = objects.updateById(object?.id as string, {
      value: {
        llm_compare: newValue,
      },
    }) as StorageObject<ComparisonObjectValue>;
    const updatedComparisonObjects = replaceItemById(
      comparisonObjects,
      newComparisonObject as StorageObject<ComparisonObjectValue>,
    );
    dispatch(
      setSelectedComparisonObject(
        newComparisonObject as StorageObject<ComparisonObjectValue>,
      ),
    );
    dispatch(setComparisonObjectValue(newValue));
    dispatch(setComparisonObjects(updatedComparisonObjects));
    return newComparisonObject;
  };

  const processComparisonChat = useCallback(
    (objectValues: CompareObject[]) => {
      if (objectValues.length === 0) {
        return;
      }
      dispatch(createChatComparison(objectValues))
        .unwrap()
        .then((response: any) => {
          const currentValue = processingRef.current || [];
          const currentChat = currentValue?.find(
            (completion) => completion.id === response.id,
          );
          const systemMessage =
            response.status === "fulfilled"
              ? (response?.value.choices[0].message?.content as string)
              : `Error: ${response.reason}`;
          const compareChat = {
            ...currentChat,
            messages: addNewMessageObject(
              currentChat?.messages as ChatCompletionsMessageObject[],
              "assistant",
              systemMessage,
            ),
          };
          const newValue = currentValue.map((compare) => {
            if (compare.id === response.id) {
              return {
                ...compareChat,
                isAssistantChatMessageLoading: false,
              };
            }
            return compare;
          });

          processingRef.current = newValue as CompareObject[];

          const newComparisonObject = updateObjectsWithNewValue(
            selectedComparisonObject!,
            newValue as CompareObject[],
          );
          dispatch(updateComparisonObjectAction(newComparisonObject));
          const filteredValue = objectValues.filter(
            (value) => value.id !== response.id,
          );
          processComparisonChat(filteredValue as CompareObject[]);
        });
    },
    [dispatch, objectValue],
  );

  const handleUserMessageChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setUserMessage(e.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.ctrlKey && event.key === "Enter") {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleSendMessage = () => {
    const checkModelExist = objectValue.findIndex(
      (obj) => obj.model === "" || obj.model === undefined,
    );
    if (checkModelExist !== -1) {
      dispatch(
        createNotification(
          "warning",
          `${t("llm.no_model_selected")}${checkModelExist + 1}`,
        ),
      );
      return;
    }
    const newValue = objectValue.map((compareChat) => {
      const messages = compareChat.messages;
      const instruction = compareChat.instruction
        ? `${compareChat.instruction} ${compliance}`
        : `${compliance}`;
      const newMessages =
        compareChat.system_role_name !== "user"
          ? addNewMessageObject(
              messages,
              compareChat?.system_role_name as string,
              instruction,
            )
          : deleteInstructions(messages);
      return {
        ...compareChat,
        messages: addNewMessageObject(newMessages, "user", userMessage),
      };
    });

    dispatch(setComparisonObjectValue(newValue as CompareObject[]));
    setUserMessage("");
    processingRef.current = newValue;
    processComparisonChat(newValue as CompareObject[]);
  };

  const addNewChatComparison = () => {
    if (columns < 4) setColumns(columns + 1);
    const instruction = objectValue.find(
      (item) => item.instruction,
    )?.instruction;
    const newChatObject = {
      id: generateUniqueId(),
      model: "",
      system_role_name: "",
      messages: [],
      instruction: instruction ? instruction : "",
    };
    const newValue = [...objectValue, newChatObject];
    const newObject = updateObjectsWithNewValue(
      selectedComparisonObject!,
      newValue,
    );
    dispatch(updateComparisonObjectAction(newObject));
  };

  const handleInstructionsChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setInstruction(e.target.value);
    const newValue = objectValue.map((completion: CompareObject) => {
      if (completion.system_role_name === "user") {
        return completion;
      } else {
        return {
          ...completion,
          instruction: e.target.value,
        };
      }
    });
    const newObject = updateObjectsWithNewValue(
      selectedComparisonObject!,
      newValue,
    );
    updateComparisonObject(newObject);
  };

  return {
    userMessage,
    columns,
    instruction,
    setColumns,
    handleKeyDown,
    handleSendMessage,
    handleUserMessageChange,
    addNewChatComparison,
    handleInstructionsChange,
    updateObjectsWithNewValue,
    updateComparisonObject,
  };
};
